import { ApiError, type ProblemDetails } from '@/lib/api/api-error';
import type { ApiEnvelope } from '@/lib/api/types';
import { env } from '@/lib/env';
import { useSessionStore } from '@/stores/session-store';

type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /** Skips both the Authorization header and the 401 refresh retry. */
  anonymous?: boolean;
}

interface RefreshResponse {
  accessToken: string;
  expiresInSeconds: number;
}

const REFRESH_PATH = '/auth/refresh';
const CLOCK_SKEW_MS = 30_000;

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, '');
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;

  if (!query) {
    return url;
  }

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  }

  const serialised = params.toString();
  return serialised ? `${url}?${serialised}` : url;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    return await response.text();
  }

  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

function toApiError(response: Response, body: unknown): ApiError {
  const problem = (
    typeof body === 'object' && body !== null ? body : null
  ) as ProblemDetails | null;
  const message =
    problem?.title ?? problem?.detail ?? `Request failed with status ${String(response.status)}`;
  return new ApiError(message, response.status, problem);
}

function unwrap(body: unknown): unknown {
  if (typeof body === 'object' && body !== null && 'data' in body) {
    return (body as ApiEnvelope<unknown>).data;
  }
  return body;
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Placeholder refresh flow. The refresh token is an HttpOnly + Secure +
 * SameSite=Strict cookie, so the browser attaches it via credentials: 'include'
 * and JavaScript never reads or writes it. Single-flight: concurrent 401s share
 * one refresh call. Wire the real response shape once the endpoint is built.
 */
async function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= (async (): Promise<boolean> => {
    try {
      const response = await fetch(buildUrl(REFRESH_PATH), {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'application/json' },
      });

      if (!response.ok) {
        useSessionStore.getState().clearSession();
        return false;
      }

      const payload = unwrap(await parseBody(response)) as RefreshResponse;
      useSessionStore
        .getState()
        .setAccessToken(payload.accessToken, Date.now() + payload.expiresInSeconds * 1000);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

function isAccessTokenExpired(): boolean {
  const { accessToken, accessTokenExpiresAt } = useSessionStore.getState();
  return accessToken !== null && accessTokenExpiresAt !== null
    ? accessTokenExpiresAt - CLOCK_SKEW_MS <= Date.now()
    : false;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { accessToken } = useSessionStore.getState();

  const headers = new Headers({ accept: 'application/json' });

  if (!options.anonymous && accessToken !== null) {
    headers.set('authorization', `Bearer ${accessToken}`);
  }

  if (options.body !== undefined) {
    headers.set('content-type', 'application/json');
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers,
  };

  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  if (options.signal) {
    init.signal = options.signal;
  }

  try {
    return await fetch(buildUrl(path, options.query), init);
  } catch (cause) {
    throw ApiError.network(cause);
  }
}

export async function request<TData>(path: string, options: RequestOptions = {}): Promise<TData> {
  if (!options.anonymous && isAccessTokenExpired()) {
    await refreshAccessToken();
  }

  let response = await send(path, options);

  if (response.status === 401 && !options.anonymous) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      throw toApiError(response, await parseBody(response));
    }
    response = await send(path, options);
  }

  const body = await parseBody(response);

  if (!response.ok) {
    throw toApiError(response, body);
  }

  return unwrap(body) as TData;
}

export const api = {
  get: <TData>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<TData>(path, { ...options, method: 'GET' }),
  post: <TData>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<TData>(path, { ...options, method: 'POST', body }),
  put: <TData>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<TData>(path, { ...options, method: 'PUT', body }),
  patch: <TData>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<TData>(path, { ...options, method: 'PATCH', body }),
  delete: <TData>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<TData>(path, { ...options, method: 'DELETE' }),
};
